import { ExternalLink, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { type StorePopupRecord } from '../hooks/api';

const DAY_IN_MS = 24 * 60 * 60 * 1000;

const getSafeRedirectUrl = (value?: string | null, appendStoreEmail = false, storeEmail?: string) => {
	if (!value?.trim()) return null;
	try {
		const url = new URL(value.trim());
		if (!['http:', 'https:'].includes(url.protocol)) return null;
		if (appendStoreEmail && storeEmail) url.searchParams.set('ID', storeEmail);
		return url.toString();
	} catch {
		return null;
	}
};

const getStorageKey = (popup: StorePopupRecord, country: string) =>
	`store-popup-dismissed:${country}:${popup.id}:${popup.display_version}`;

export function StorePopup({
	popup,
	country,
	storeEmail,
	preview = false
}: {
	popup: StorePopupRecord;
	country: string;
	storeEmail?: string;
	preview?: boolean;
}) {
	const storageKey = useMemo(() => getStorageKey(popup, country), [country, popup]);
	const [isOpen, setIsOpen] = useState(preview);
	const redirectUrl = getSafeRedirectUrl(popup.redirect_url, popup.append_store_email_to_url, storeEmail);

	useEffect(() => {
		if (preview) {
			setIsOpen(true);
			return;
		}

		if (popup.is_active === false || !popup.title?.trim()) {
			setIsOpen(false);
			return;
		}

		let dismissedAt = 0;
		try {
			dismissedAt = Number(window.localStorage.getItem(storageKey));
		} catch {
			// If browser storage is unavailable, showing the active popup is safer than hiding it indefinitely.
		}
		const intervalMs = Math.max(1, popup.dismiss_interval_days || 1) * DAY_IN_MS;
		setIsOpen(!dismissedAt || Date.now() - dismissedAt >= intervalMs);
	}, [popup, preview, storageKey]);

	if (!isOpen) return null;

	const dismiss = () => {
		if (!preview) {
			try {
				window.localStorage.setItem(storageKey, Date.now().toString());
			} catch {
				// The popup should remain closable even when browser storage is unavailable.
			}
		}
		setIsOpen(false);
	};

	const openLink = () => {
		if (!redirectUrl) return;
		dismiss();
		window.open(redirectUrl, '_blank', 'noopener,noreferrer');
	};

	return (
		<div
			className={
				preview
					? 'relative flex min-h-[420px] items-center justify-center rounded-xl bg-black/60 p-4'
					: 'fixed inset-0 z-[9500] flex items-end justify-center bg-black/70 p-0 backdrop-blur-[1.5px] md:items-center md:p-6'
			}
		>
			<section
				role="dialog"
				data-store-popup-dialog="true"
				aria-modal={!preview}
				aria-labelledby={`store-popup-title-${popup.id}`}
				className="relative max-h-[92vh] w-full overflow-y-auto rounded-t-3xl bg-neutral-1000 text-neutral-100 shadow-2xl md:max-w-lg md:rounded-2xl"
			>
				<div className="mx-auto mt-3 h-1 w-12 rounded-full bg-neutral-700 md:hidden" />
				<button
					type="button"
					onClick={dismiss}
					className="absolute right-4 top-4 z-10 flex size-9 items-center justify-center rounded-full bg-neutral-950/75 text-neutral-200 transition-colors hover:bg-neutral-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
					aria-label="Cerrar comunicado"
				>
					<X className="size-5" />
				</button>

				{popup.image_url && (
					<button
						type="button"
						onClick={openLink}
						disabled={!redirectUrl}
						className={`block w-full overflow-hidden md:rounded-t-2xl ${redirectUrl ? 'cursor-pointer' : 'cursor-default'}`}
						aria-label={redirectUrl ? 'Abrir enlace del comunicado' : undefined}
					>
						<img src={popup.image_url} alt="" className="max-h-[48vh] w-full object-cover" />
					</button>
				)}

				<div className="space-y-3 p-6 pt-5 md:p-8 md:pt-6">
					<h2
						id={`store-popup-title-${popup.id}`}
						className="pr-8 text-xl font-semibold leading-tight text-neutral-100 md:text-2xl"
					>
						{popup.title}
					</h2>
					{popup.subtitle && (
						<p className="whitespace-pre-wrap text-sm leading-relaxed text-neutral-400 md:text-base">{popup.subtitle}</p>
					)}
					{redirectUrl && (
						<button
							type="button"
							onClick={openLink}
							className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-neutral-1000"
						>
							{popup.button_text?.trim() || 'Ver más'}
							<ExternalLink className="size-4" />
						</button>
					)}
					{!redirectUrl && (
						<button
							type="button"
							onClick={dismiss}
							className="mt-2 w-full rounded-lg border border-neutral-800 px-4 py-3 text-sm font-semibold text-neutral-100 transition-colors hover:bg-neutral-925 focus:outline-none focus:ring-2 focus:ring-primary-500"
						>
							Entendido
						</button>
					)}
				</div>
			</section>
		</div>
	);
}
